import React, { useEffect, useMemo } from 'react';
import { Box, ListItem, Typography } from '@mui/material';
import { DragIndicator as DragIcon } from '@mui/icons-material';
import { useDrag } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { alpha } from '@mui/material/styles';
import { Transaction } from '../../types';

interface DraggableTransactionProps {
  transaction: Transaction;
}

export const DraggableTransaction: React.FC<DraggableTransactionProps> = React.memo(({ transaction }) => {
  const [{ isDragging }, drag, preview] = useDrag<Transaction, unknown, { isDragging: boolean }>({
    type: 'transaction',
    item: transaction,
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    })
  });

  // Use empty preview
  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);

  const formattedAmount = useMemo(() => {
    const absAmount = Math.abs(transaction.amount);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(absAmount);
  }, [transaction.amount]);

  const getAmountDisplay = useMemo(() => {
    const isNegative = transaction.amount < 0;
    
    if (transaction.type === 'expense') {
      // Expenses are always shown as negative
      return `-${formattedAmount}`;
    } else if (transaction.type === 'income') {
      // Income is always shown as positive
      return `+${formattedAmount}`;
    } else if (transaction.type === 'savings') {
      // Savings: positive for deposits, negative for withdrawals
      return isNegative ? `-${formattedAmount}` : `+${formattedAmount}`;
    }
    
    // Default fallback
    return isNegative ? `-${formattedAmount}` : `+${formattedAmount}`;
  }, [transaction.amount, transaction.type, formattedAmount]);

  const formattedDate = useMemo(() => {
    return new Date(transaction.date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  }, [transaction.date]);

  // Helper function to get amount color based on transaction type and amount
  const getAmountColor = (type: string, amount: number) => {
    if (type === 'expense') {
      return 'error';
    } else if (type === 'income') {
      return 'success';
    } else if (type === 'savings') {
      // Savings: red for withdrawals (negative), primary for deposits (positive)
      return amount < 0 ? 'error' : 'primary';
    }
    return 'text.primary';
  };

  // Helper function to get badge styles based on transaction type and amount
  const getBadgeStyles = (type: string, amount: number) => {
    if (type === 'income') {
      return {
        backgroundColor: (theme: any) => alpha(theme.palette.success.main, 0.1),
        color: (theme: any) => theme.palette.success.main
      };
    } else if (type === 'expense') {
      return {
        backgroundColor: (theme: any) => alpha(theme.palette.error.main, 0.1),
        color: (theme: any) => theme.palette.error.main
      };
    } else if (type === 'savings') {
      // Savings: red for withdrawals (negative), primary for deposits (positive)
      if (amount < 0) {
        return {
          backgroundColor: (theme: any) => alpha(theme.palette.error.main, 0.1),
          color: (theme: any) => theme.palette.error.main
        };
      } else {
        return {
          backgroundColor: (theme: any) => alpha(theme.palette.primary.main, 0.1),
          color: (theme: any) => theme.palette.primary.main
        };
      }
    }
    return {
      backgroundColor: (theme: any) => alpha(theme.palette.grey[500], 0.1),
      color: (theme: any) => theme.palette.grey[500]
    };
  };

  return (
    <ListItem
      ref={drag}
      sx={{
        borderBottom: '1px solid',
        borderColor: isDragging ? 'primary.main' : 'divider',
        '&:last-child': { borderBottom: 'none' },
        opacity: isDragging ? 0.5 : 1,
        cursor: 'move',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: 1,
        py: 1.5,
        userSelect: 'none'
      }}
      data-tour={transaction.isDemo ? "demo-transaction" : undefined}

    >
      <Box 
        display="flex" 
        width="100%" 
        alignItems="center"
      >
        <DragIcon sx={{ mr: 1, color: 'text.secondary' }} />
        <Typography variant="body1" sx={{ flex: 1 }}>
          {transaction.description}
        </Typography>
        <Typography
          variant="body2"
          color={getAmountColor(transaction.type, transaction.amount)}
          sx={{ fontWeight: 'medium' }}
        >
          {getAmountDisplay}
        </Typography>
      </Box>
      <Box display="flex" justifyContent="space-between" width="100%" alignItems="center">
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', ml: 4 }}>
          {formattedDate}
        </Typography>
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            ...getBadgeStyles(transaction.type, transaction.amount),
            borderRadius: '16px',
            px: 1,
            py: 0.25,
            fontSize: '0.75rem',
            fontWeight: 500,
          }}
        >
          {transaction.category}
        </Box>
      </Box>
    </ListItem>
  );
}); 